<!-- ═══════════════════════════════════════════════════════════
     DOCUMENT UPLOAD MASTER
     Two exclusive modes:
       editMode=true  → Add Document form (full content replacement)
       editMode=false → Toolbar + file grid/list
══════════════════════════════════════════════════════════════ -->

<!-- ══════════════════════════════════════════
     ADD / EDIT MODE  (replaces file view)
══════════════════════════════════════════ -->
<div class="doc-add-view" *ngIf="editMode">

  <!-- Header row: back arrow + title -->
  <div class="doc-add-view__header">
    <button class="doc-add-view__back" (click)="editMode = false" type="button">
      <i class="bi bi-arrow-left me-1"></i>Back
    </button>
    <span class="doc-add-view__title">
      <i class="bi bi-plus-circle me-2 text-primary"></i>Add Document
    </span>
  </div>

  <!-- UIC Form rendered flat — no extra wrapper card -->
  <app-uic-form #uicFormRef
    [gFormUIC]="uicForm"
    [modalHeader]="'Document Details'"
    (onSubmitEvent)="submitUICForm()"
    (onCloseEvent)="editMode = false">
  </app-uic-form>

</div>

<!-- ══════════════════════════════════════════
     BROWSE MODE  (toolbar + file list/grid)
══════════════════════════════════════════ -->
<ng-container *ngIf="!editMode">

  <!-- ── Toolbar ── -->
  <div class="doc-toolbar">
    <div class="doc-toolbar__left">
      <span class="doc-toolbar__title">
        <i class="bi bi-paperclip me-1"></i>Documents
      </span>
      <span class="doc-toolbar__badge" *ngIf="documentData.length > 0">
        {{ documentData.length }}
      </span>
    </div>
    <div class="doc-toolbar__right">

      <!-- View Toggle -->
      <div class="doc-view-toggle" title="Switch view">
        <button class="doc-view-btn" [class.active]="currentView === 'card'"
                (click)="setView('card')" title="Grid view" type="button">
          <i class="bi bi-grid-fill"></i>
        </button>
        <button class="doc-view-btn" [class.active]="currentView === 'list'"
                (click)="setView('list')" title="List view" type="button">
          <i class="bi bi-list-ul"></i>
        </button>
      </div>

      <!-- Search -->
      <div class="doc-search-wrap">
        <span class="doc-search-icon-wrap"><i class="bi bi-search"></i></span>
        <input class="doc-search-input"
               type="text"
               placeholder="Search documents…"
               [formControl]="searchForm.controls.whereClause"
               (keyup.enter)="loadData()" />
        <button class="doc-search-clear"
                *ngIf="searchForm.controls.whereClause.value"
                (click)="clearSearchAndReload()"
                title="Clear"
                type="button">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Add New -->
      <button class="btn btn-sm btn-primary d-flex align-items-center gap-1"
              (click)="addDocument()" type="button">
        <i class="bi bi-plus-lg"></i><span>New</span>
      </button>

    </div>
  </div><!-- /toolbar -->

  <!-- ══════════════════════════════════════════
       MOBILE CAMERA CAPTURE  (hidden ≥ 576 px)
       Two tap-friendly buttons open the device
       camera directly for photos and videos.
       File is uploaded + saved without a form.
  ══════════════════════════════════════════ -->
  <div class="d-sm-none">

    <!-- Capture buttons (visible while not uploading) -->
    <div class="doc-capture-bar" *ngIf="!captureUploading">
      <button class="doc-capture-btn doc-capture-btn--photo"
              (click)="photoCaptureRef.click()" type="button">
        <i class="bi bi-camera-fill"></i>
        <span>Take Photo</span>
      </button>
      <button class="doc-capture-btn doc-capture-btn--video"
              (click)="videoCaptureRef.click()" type="button">
        <i class="bi bi-camera-video-fill"></i>
        <span>Record Video</span>
      </button>

      <!-- Hidden inputs — capture="environment" opens rear camera on mobile -->
      <input #photoCaptureRef type="file" accept="image/*"
             capture="environment" hidden
             (change)="onCaptureSelected($event)">
      <input #videoCaptureRef type="file" accept="video/*"
             capture="environment" hidden
             (change)="onCaptureSelected($event)">
    </div>

    <!-- Upload progress (replaces capture buttons while uploading) -->
    <div class="doc-capture-upload" *ngIf="captureUploading">
      <div class="doc-capture-upload__info">
        <i class="bi bi-cloud-upload-fill me-1"></i>
        <span>Uploading {{ captureProgress }}%…</span>
      </div>
      <div class="progress doc-capture-upload__bar">
        <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
             role="progressbar"
             [style.width.%]="captureProgress || 0"
             [attr.aria-valuenow]="captureProgress || 0"
             aria-valuemin="0" aria-valuemax="100">
        </div>
      </div>
    </div>

  </div><!-- /mobile capture -->

  <!-- ── Top pagination: page-size selector — desktop only ── -->
  <div class="d-none d-sm-block">
    <app-pagination
      [EnablePageSize]="true"
      [rowData]="documentData"
      (pageSizeChanged)="handlePageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- ══════════════════════
       LIST VIEW
  ══════════════════════ -->
  <ng-container *ngIf="currentView !== 'card'">
    <div class="doc-list-wrap">
      <app-uic-color-table
        [uicListData]="uicList"
        [tableData]="documentData"
        [enableActionColumn]="true"
        [enableAutoSerial]="true"
        [actionColumnLabel]="'Action'"
        [actionOptions]="[
          { actionName: 'View',   actionIcon: 'fa fa-eye' },
          { actionName: 'Delete', actionIcon: 'fa fa-trash' }
        ]"
        (actionClick)="handleActionClick($event)">
      </app-uic-color-table>
    </div>
  </ng-container>

  <!-- ══════════════════════
       CARD / GRID VIEW
  ══════════════════════ -->
  <ng-container *ngIf="currentView === 'card'">

    <!-- Empty state -->
    <div class="doc-empty-state" *ngIf="documentData.length === 0">
      <div class="doc-empty-state__icon">
        <i class="bi bi-folder2-open"></i>
      </div>
      <p class="doc-empty-state__title">No documents yet</p>
      <p class="doc-empty-state__sub">Upload your first document to get started</p>
      <button class="btn btn-sm btn-primary" (click)="addDocument()" type="button">
        <i class="bi bi-plus-lg me-1"></i>Upload Document
      </button>
    </div>

    <!-- File grid -->
    <div class="doc-file-grid" *ngIf="documentData.length > 0">
      <div class="doc-file-card"
           *ngFor="let f of documentData"
           (click)="handleActionClick({ actionName: 'View', rowData: f })">

        <!-- Icon area -->
        <div class="dfc-icon-area" [ngClass]="getFileTypeClass(f)">
          <i [class]="getFileTypeIcon(f)"></i>
        </div>

        <!-- File info -->
        <div class="dfc-info">
          <p class="dfc-name" [title]="getFileName(f)">{{ getFileName(f) }}</p>
          <p class="dfc-meta">
            <span class="dfc-ext-badge">{{ getFileExtension(f) }}</span>
            <span *ngIf="f.DocType || f.docType" class="dfc-type">
              {{ f.DocType || f.docType }}
            </span>
          </p>
          <p class="dfc-date" *ngIf="f.CreatedOn || f.createdOn">
            <i class="bi bi-clock me-1"></i>{{ formatDocDate(f.CreatedOn || f.createdOn) }}
          </p>
        </div>

        <!-- Actions -->
        <div class="dfc-actions" (click)="$event.stopPropagation()">
          <button class="dfc-btn dfc-btn--view" title="Preview" type="button"
                  (click)="handleActionClick({ actionName: 'View', rowData: f })">
            <i class="bi bi-eye-fill"></i>
          </button>
          <a class="dfc-btn dfc-btn--download" title="Download"
             [href]="f.DocURL || f.docURL || '#'"
             [attr.download]="getFileName(f)"
             target="_blank"
             (click)="$event.stopPropagation()">
            <i class="bi bi-download"></i>
          </a>
          <button class="dfc-btn dfc-btn--delete" title="Delete" type="button"
                  (click)="handleActionClick({ actionName: 'Delete', rowData: f })">
            <i class="bi bi-trash3-fill"></i>
          </button>
        </div>

      </div><!-- /doc-file-card -->
    </div><!-- /doc-file-grid -->

  </ng-container>

  <!-- ── Bottom pagination: navigation — desktop only ── -->
  <div class="d-none d-sm-block">
    <app-pagination
      [EnableNavigation]="true"
      [rowData]="documentData"
      (pageSizeChanged)="handlePageSizeChange($event)">
    </app-pagination>
  </div>

</ng-container><!-- /browse mode -->


<!-- ══════════════════════════════════════════
     PREVIEW MODAL  (always in DOM, toggled via style.display)
══════════════════════════════════════════ -->
<div #modal class="doc-preview-modal">
  <div class="doc-preview-modal__backdrop" (click)="closePreview()"></div>
  <div class="doc-preview-modal__dialog">

    <!-- Header -->
    <div class="doc-preview-modal__header">
      <span class="doc-preview-modal__title">
        <i class="bi bi-eye me-2"></i>Document Preview
      </span>
      <div class="doc-preview-modal__actions">
        <button class="doc-preview-modal__btn" *ngIf="fileType !== 'empty'"
                (click)="openInNewWindow()" title="Open in new window" type="button">
          <i class="bi bi-box-arrow-up-right me-1"></i>Open
        </button>
        <button class="doc-preview-modal__close"
                (click)="closePreview()" title="Close" type="button">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="doc-preview-modal__body">

      <img *ngIf="fileType === 'image'"   [src]="previewUrl" class="doc-preview-modal__img" />

      <iframe *ngIf="fileType === 'pdf'"    [src]="previewUrl" class="doc-preview-modal__frame"></iframe>
      <iframe *ngIf="fileType === 'doc'"    [src]="previewUrl" class="doc-preview-modal__frame"></iframe>
      <iframe *ngIf="fileType === 'office'" [src]="previewUrl" class="doc-preview-modal__frame"></iframe>
      <iframe *ngIf="fileType === 'html'"   [src]="previewUrl" class="doc-preview-modal__frame"></iframe>

      <pre *ngIf="fileType === 'text'" class="doc-preview-modal__text">{{ textContent }}</pre>

      <div *ngIf="fileType === 'video'">
        <video *ngIf="!videoError" controls class="doc-preview-modal__video">
          <source [src]="previewUrl" />
        </video>
        <div *ngIf="videoError" class="doc-preview-modal__unsupported">
          <i class="bi bi-exclamation-circle text-warning"></i>
          <p>Your browser cannot play this video format.</p>
          <p class="text-muted small">AVI, MOV and MKV require a media player like VLC.</p>
          <a [href]="urlForWindow" target="_blank" download class="btn btn-sm btn-primary">
            <i class="bi bi-download me-1"></i>Download to Watch
          </a>
        </div>
      </div>

      <div *ngIf="fileType === 'audio'" class="doc-preview-modal__audio">
        <i class="bi bi-music-note-beamed doc-preview-modal__audio-icon"></i>
        <audio controls><source [src]="previewUrl" /></audio>
      </div>

      <div *ngIf="fileType === 'other'" class="doc-preview-modal__unsupported">
        <i class="bi bi-file-earmark-arrow-down"></i>
        <p>No preview available for this file type.</p>
        <a [href]="previewUrl" target="_blank" download class="btn btn-sm btn-primary">
          <i class="bi bi-download me-1"></i>Download File
        </a>
      </div>

      <div *ngIf="fileType === 'empty'" class="doc-preview-modal__unsupported">
        <i class="bi bi-folder2-open text-muted"></i>
        <p class="text-muted">No file has been uploaded for this document.</p>
      </div>

    </div><!-- /body -->
  </div><!-- /dialog -->
</div><!-- /modal -->
